import {responsive, type ResponsiveValue} from "./responsive";
import type {PaletteColor} from "./color";

export type BorderSize = "sm" | "md" | "lg";

// Tokens only, deliberately: everything here becomes a Tailwind class, so borders theme with
// data-theme, take breakpoint objects, and stay overridable by className. A one-off belongs in
// className as an arbitrary value (`border-b-[3px] border-[#01EAC1]`) — the caller writes that
// literal in their own source, so JIT finds it without any of the safelist machinery below.
export interface BorderProps {
	border?: ResponsiveValue<BorderSize>;
	borderTop?: ResponsiveValue<BorderSize>;
	borderRight?: ResponsiveValue<BorderSize>;
	borderBottom?: ResponsiveValue<BorderSize>;
	borderLeft?: ResponsiveValue<BorderSize>;
	borderWidth?: ResponsiveValue<BorderSize>;
	borderRadius?: ResponsiveValue<BorderSize>;
	borderColor?: ResponsiveValue<PaletteColor>;
}

/**
 * Splits the border props off a prop bag into Tailwind classes and a DOM-safe remainder. Mirrors
 * `splitSpacing` — without it, `borderRadius` and friends reach the element as unknown React
 * attributes.
 */
export function splitBorder<P extends BorderProps>(props: P): {className: string, rest: Omit<P, keyof BorderProps>} {
	const rest = {...props} as Record<string, unknown>;
	for (const prop of BORDER_PROPS) delete rest[prop];

	const widths = Object.entries(SIDE_SUFFIX)
		.map(([prop, side]) => responsive(props[prop as BorderSideProp], size => `border${side}${WIDTH_SUFFIX[size]}`))
		.concat(responsive(props.borderWidth, size => `border${WIDTH_SUFFIX[size]}`))
		.filter(Boolean);

	// A width with no colour would inherit whatever the cascade hands it, so default to the
	// Separator colour whenever one is drawn.
	const color = props.borderColor === undefined
		? (widths.length ? `border-${DEFAULT_BORDER_COLOR}` : "")
		: responsive(props.borderColor, value => `border-${value}`);

	const className = [...widths, responsive(props.borderRadius, size => `rounded-${size}`), color]
		.filter(Boolean)
		.join(" ");

	return {className, rest: rest as Omit<P, keyof BorderProps>};
}

const DEFAULT_BORDER_COLOR: PaletteColor = "foreground-400";

const SIDE_SUFFIX = {
	border: "",
	borderTop: "-t",
	borderRight: "-r",
	borderBottom: "-b",
	borderLeft: "-l"
} as const;

type BorderSideProp = keyof typeof SIDE_SUFFIX;

const BORDER_PROPS = [...Object.keys(SIDE_SUFFIX), "borderWidth", "borderRadius", "borderColor"];

const WIDTH_SUFFIX: Record<BorderSize, string> = {sm: "", md: "-2", lg: "-4"};
